import React, { FC, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Link from '@mui/material/Link'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Video, PublicationInfo, ChainAnchor } from '../../types'
import { getVideo } from '../../system/apitools'
import SectionWrapper, { SectionStatus } from './LockedSectionWrapper'
import ChainAnchorBlock from './ChainAnchorBlock'
import { explorerUrl, type Cluster } from '../../solana/kit'
import { clusterRpcUrl } from '../../solana/endpoints'

// Mirrors the fiber.Map returned by GET /publish-status.
interface PublishStatus {
  state: string
  error?: string
  percent?: number
  stage?: string
  speed?: string
  logs?: string[]
  publication?: PublicationInfo
  revision?: number
  window_expires?: string
}

interface SealResponse {
  video: Video
  chain_status: string
}

interface PublicationSectionProps {
  video: Video
  isReady: boolean
}

const formatBytes = (bytes?: number): string => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`
}

const formatDate = (iso?: string): string => {
  if (!iso) return 'unknown time'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'unknown time'
  return d.toLocaleString()
}

// Days/hours (or hours/minutes, or just minutes in the last hour) — never a
// raw timestamp, since the whole point is a number a publisher can act on.
const formatCountdown = (remainingMs: number): string => {
  if (remainingMs <= 0) return 'under a minute'
  const totalMinutes = Math.floor(remainingMs / 60000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days} day${days === 1 ? '' : 's'}, ${hours} hour${hours === 1 ? '' : 's'}`
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'}, ${minutes} minute${minutes === 1 ? '' : 's'}`
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

// The single most important line in this component: a "sealed" video today
// is enforced by nothing but this server's own data store. Shown in every
// state, at the same prominent spot, so it can never be missed or softened
// in one branch and not another.
const UNENFORCED_NOTICE =
  'Publication is not yet anchored to a blockchain. A "sealed" video today is enforced only ' +
  "inside this server's own database — nobody outside this team can check it independently, " +
  'and someone with direct database access could still change it. This becomes a real, ' +
  'independently-checkable guarantee once the on-chain program takes over in a later phase.'

// Once video.publication.chain exists, the notice above is simply false: a
// root hash IS anchored on an external program this server does not
// control. Wording is cluster-aware on purpose — localnet is a validator on
// this machine with no durability of its own, so the honest claim there is
// "changing it now leaves evidence", not "this is forever". That stronger
// claim only becomes true on a durable public cluster (devnet for testing,
// mainnet for real).
const chainAnchoredNotice = (chain: ChainAnchor): string => {
  const isLocal = chain.cluster === 'localnet'
  return (
    `This video's root hash is anchored on ${chain.cluster}` +
    (isLocal
      ? ' (a local validator on this machine — not yet a durable, public record).'
      : '.') +
    ' Once past the correction window, the on-chain program refuses any transaction that would ' +
    'change it — including from us. Verifying no longer requires trusting this server: read the ' +
    'video account directly.'
  )
}

const PublicationSection: FC<PublicationSectionProps> = ({ video, isReady }) => {
  const queryClient = useQueryClient()
  const logEndRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState<PublishStatus | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmSealOpen, setConfirmSealOpen] = useState(false)
  const [confirmRepublishOpen, setConfirmRepublishOpen] = useState(false)
  const [forceFull, setForceFull] = useState(false)
  const [reverifyJobId, setReverifyJobId] = useState<string | null>(null)

  // Live "now", corrected for clock skew between this browser and the
  // server. Only ticks while there is a countdown worth showing — once the
  // video is sealed (explicitly or by expiry) there is nothing left to count
  // down to.
  const clockSkewMs = useRef(0)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const state = video.publication_state || ''
  const info = video.publication
  const hasInfo = !!info
  const isPublishing = state === 'publishing'
  const isFailed = state === 'publish_failed'
  const explicitlySealed = state === 'sealed'

  const windowExpiresDate = info?.window_expires ? new Date(info.window_expires) : null
  const expiredByWindow = !!windowExpiresDate && nowMs > windowExpiresDate.getTime()
  const sealedInEffect = hasInfo && (explicitlySealed || expiredByWindow)

  // Mutually exclusive phases, in priority order: a job actually running or
  // failed always wins over a derived "sealed by expiry" reading, because
  // FailPublish itself refuses to record a failure against a truly sealed
  // video (see api/pkg/data/publication.go) — isFailed and sealedInEffect
  // never really disagree about which state the video is "really" in.
  type Phase = 'draft' | 'publishing' | 'failed' | 'sealed' | 'published'
  let phase: Phase
  if (isPublishing) phase = 'publishing'
  else if (isFailed) phase = 'failed'
  else if (sealedInEffect) phase = 'sealed'
  else if (hasInfo) phase = 'published'
  else phase = 'draft'

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [progress?.logs?.length])

  // Publishing: poll status every second, same shape/cadence as
  // DiarizationSection's driving effect in pages/Video.tsx. Also the point
  // where we read the server's own clock (the HTTP Date header) to correct
  // for skew, since every poll response carries one for free.
  useEffect(() => {
    if (!isPublishing) return
    const poll = async () => {
      try {
        const res = await axios.get<PublishStatus>(`/api/v1/videos/${video.id}/publish-status?nolog`)
        applySkewFromHeaders(res.headers)
        setProgress(res.data)
        if (res.data.state === 'published' || res.data.state === 'publish_failed') {
          queryClient.invalidateQueries({ queryKey: ['video', video.id] })
          setProgress(null)
        }
      } catch {
        /* ignore */
      }
    }
    poll()
    const interval = setInterval(poll, 1000)
    return () => clearInterval(interval)
  }, [isPublishing, video.id, queryClient])

  const applySkewFromHeaders = (headers: any) => {
    const dateHeader = headers?.date
    if (!dateHeader) return
    const serverMs = new Date(dateHeader).getTime()
    if (!Number.isNaN(serverMs)) clockSkewMs.current = serverMs - Date.now()
  }

  // Published: a one-off request to learn the server's clock (so the
  // countdown never confidently shows the wrong side of expiry just because
  // this browser's clock is off), then a plain client-side ticker — no
  // reason to poll the server every second just to count down a timer.
  useEffect(() => {
    if (phase !== 'published') return
    let cancelled = false
    axios
      .get(`/api/v1/videos/${video.id}/publish-status?nolog`)
      .then(res => {
        if (!cancelled) {
          applySkewFromHeaders(res.headers)
          setNowMs(Date.now() + clockSkewMs.current)
        }
      })
      .catch(() => {
        /* ignore — countdown just runs on the browser's own clock */
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, video.id])

  useEffect(() => {
    if (phase !== 'published') return
    const tick = () => setNowMs(Date.now() + clockSkewMs.current)
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [phase])

  // Re-verify: there is no progress route for this job (D6 in the plan), so
  // we poll the video itself and watch the job-history entry we just started
  // move out of "running". A completed job that leaves host_verified_url
  // still mismatched means the check ran and found the current host doesn't
  // match — informational only, never blocking anything.
  useEffect(() => {
    if (!reverifyJobId) return
    const poll = async () => {
      try {
        const updated = await getVideo(video.id)
        const job = updated.job_history?.find(j => j.id === reverifyJobId)
        if (job && job.status !== 'running') {
          queryClient.setQueryData(['video', video.id], updated)
          setReverifyJobId(null)
        }
      } catch {
        /* ignore */
      }
    }
    poll()
    const interval = setInterval(poll, 2000)
    return () => clearInterval(interval)
  }, [reverifyJobId, video.id, queryClient])

  const publishMutation = useMutation({
    mutationFn: (withForceFull: boolean) =>
      axios
        .post<Video>(`/api/v1/videos/${video.id}/publish`, withForceFull ? { force_full: true } : {})
        .then(res => res.data),
    onSuccess: updated => {
      queryClient.setQueryData(['video', video.id], updated)
      queryClient.invalidateQueries({ queryKey: ['video', video.id] })
    },
  })

  const sealMutation = useMutation({
    mutationFn: () => axios.post<SealResponse>(`/api/v1/videos/${video.id}/seal`).then(res => res.data),
    onSuccess: data => {
      queryClient.setQueryData(['video', video.id], data.video)
      queryClient.invalidateQueries({ queryKey: ['video', video.id] })
    },
  })

  const reverifyMutation = useMutation({
    mutationFn: () => axios.post<Video>(`/api/v1/videos/${video.id}/reverify`).then(res => res.data),
    onSuccess: updated => {
      queryClient.setQueryData(['video', video.id], updated)
      const job = updated.job_history?.find(j => j.type === 'reverify' && j.status === 'running')
      if (job) setReverifyJobId(job.id)
    },
  })

  const errorMessage = (err: unknown) =>
    (err as any)?.response?.data?.error || (err as Error).message

  const publishError = publishMutation.error ? errorMessage(publishMutation.error) : null
  const sealError = sealMutation.error ? errorMessage(sealMutation.error) : null
  const isReverifying = !!reverifyJobId || reverifyMutation.isPending

  const copyRootHash = async () => {
    if (!info) return
    try {
      await navigator.clipboard.writeText(info.root_hash)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable (non-secure origin) — the hash is selectable */
    }
  }

  // Host-unverified: whenever the current stream URL isn't the one the last
  // successful re-verify (or the publish itself, before any re-verify) last
  // confirmed. Meaningful in any state where a publication actually exists.
  const hostMismatch =
    hasInfo &&
    (phase === 'published' || phase === 'sealed' || phase === 'failed') &&
    video.stream_url !== info?.host_verified_url

  // T10: the offer to record a re-verified host as a storage location on
  // chain. Distinct from hostMismatch above — this fires once a re-verify
  // has actually SUCCEEDED (RecordHostVerified only ever runs when the check
  // comes back clean; see api/pkg/worker/reverify.go), and it stays visible
  // for that host until it is recorded, even if the stream URL has since
  // moved on again — that later drift is hostMismatch's job to flag, not
  // this banner's job to hide, since the host really was confirmed serving
  // the sealed bytes at host_verified_at. Never gated on phase the way
  // hostMismatch is: a storage location can be recorded on a published OR a
  // sealed video (§6.6), so the only gate here is "does ChainAnchorBlock
  // actually render below to offer the button" (published or sealed).
  const hostVerifiedNotRecorded =
    hasInfo &&
    (phase === 'published' || phase === 'sealed') &&
    !!info?.host_verified_url &&
    !!info?.host_verified_at &&
    !(info?.storage_anchors ?? []).some(a => a.location === info?.host_verified_url)

  // The actual button lives in ChainAnchorBlock below — it is the piece with
  // wallet and program access to sign with. This banner only says the option
  // exists; PublicationSection owns the re-verify result, not the signing.
  const hostVerifiedBanner = hostVerifiedNotRecorded && (
    <Alert severity="success" sx={{ mb: 2 }}>
      Re-verify confirmed <code>{info?.host_verified_url}</code> serves the exact bytes the
      manifest committed to ({formatDate(info?.host_verified_at)}). Recording that host as a
      storage location on chain — in the Anchor on Solana section below — notes where a copy of
      the bytes can be found. It never changes what the video is, and that distinction is exactly
      why it's allowed even after sealing.
    </Alert>
  )

  let status: SectionStatus = 'pending'
  if (isPublishing) status = 'in_progress'
  else if (hasInfo) status = 'complete'

  const rootHashBlock = info && (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        Root hash ({info.hash_algo || 'sha256'})
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Typography
          sx={{ fontFamily: 'monospace', fontSize: '0.95rem', wordBreak: 'break-all', flexGrow: 1 }}
        >
          {info.root_hash}
        </Typography>
        <Tooltip title={copied ? 'Copied' : 'Copy root hash'}>
          <IconButton size="small" onClick={copyRootHash}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <SectionWrapper title="Publication" status={status}>
      {info?.chain ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {chainAnchoredNotice(info.chain)}{' '}
          <Link
            href={explorerUrl(info.chain.cluster as Cluster, 'address', info.chain.video_pda, clusterRpcUrl())}
            target="_blank"
            rel="noopener noreferrer"
          >
            View video account
          </Link>{' '}
          (<code>{info.chain.video_pda}</code>)
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          {UNENFORCED_NOTICE}
        </Alert>
      )}

      {hostMismatch && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => reverifyMutation.mutate()}
              disabled={isReverifying}
            >
              {isReverifying ? 'Checking...' : 'Re-verify'}
            </Button>
          }
        >
          {info?.host_verified_url
            ? "This video's stream URL has changed since the host was last checked against the manifest. Re-verify confirms the new host serves the exact same bytes."
            : "This video's stream URL has never been checked against the manifest. Re-verify confirms the current host serves the exact same bytes the manifest committed to."}
          {isReverifying && ' Checking now — this can take a while for a large video.'}
        </Alert>
      )}

      {/* Draft */}
      {phase === 'draft' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Publishing downloads every segment of every quality level, hashes each one, and
            writes a manifest that fingerprints exactly what this video is right now: the video
            and audio bytes, subtitles, speaker attributions, and the title, description and
            participant details. That fingerprint — the root hash — lets anyone check later
            whether any of it has changed. You get 7 days after publishing to fix mistakes;
            republishing resets that window. After the window closes, or you seal it early, none
            of that content can ever be edited again — though where the video is hosted can still
            change, any time, forever. Expect a long job and a lot of egress: a one-hour 4K video
            is roughly 21 GB of downloads.
          </Typography>

          {!isReady && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please paste the 360° stream URL and save before publishing.
            </Alert>
          )}

          {publishError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {publishError}
            </Alert>
          )}

          {isReady && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => publishMutation.mutate(false)}
              disabled={publishMutation.isPending}
            >
              {publishMutation.isPending ? 'Starting...' : 'Publish'}
            </Button>
          )}
        </Box>
      )}

      {/* Publishing */}
      {phase === 'publishing' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{progress?.stage || 'Hashing segments...'}</Typography>
          </Box>
          {progress?.percent !== undefined && progress.percent > 0 && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress variant="determinate" value={progress.percent} />
              <Typography variant="caption" color="text.secondary">
                {progress.percent}%
              </Typography>
            </Box>
          )}
          {progress?.speed && (
            <Typography variant="caption" color="text.secondary" component="div">
              Downloaded {progress.speed}
            </Typography>
          )}
          {progress?.logs && progress.logs.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', bgcolor: '#1a1a1a', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {progress.logs.map((line, i) => (
                <div key={i} style={{ color: '#aaa', whiteSpace: 'pre' }}>{line}</div>
              ))}
              <div ref={logEndRef} />
            </Box>
          )}
        </Box>
      )}

      {/* Publish failed */}
      {phase === 'failed' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ErrorOutlineIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error">
              Publish failed: {video.publication_error || 'Unknown error'}
              {hasInfo && ' — the previous publication below still stands.'}
            </Typography>
          </Box>

          {publishError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {publishError}
            </Alert>
          )}

          <Button
            variant="contained"
            color="secondary"
            onClick={() => publishMutation.mutate(false)}
            disabled={publishMutation.isPending || !isReady}
          >
            {publishMutation.isPending ? 'Starting...' : 'Retry publish'}
          </Button>
          {!isReady && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              The 360° stream URL was cleared — paste it and save before retrying.
            </Typography>
          )}

          {hasInfo && info && (
            <Box sx={{ mt: 3 }}>
              {rootHashBlock}
              <Typography variant="body2" color="text.secondary">
                Published {formatDate(info.published_at)} · revision {info.revision}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Published — inside the correction window */}
      {phase === 'published' && info && (
        <Box>
          {rootHashBlock}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Published {formatDate(info.published_at)} · revision {info.revision} ·{' '}
            {info.rendition_count} renditions · {info.segment_count} segments ·{' '}
            {formatBytes(info.total_bytes)}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Correction window closes in <strong>{formatCountdown(windowExpiresDate!.getTime() - nowMs)}</strong>{' '}
            ({formatDate(info.window_expires)}). Republishing resets this window; after it closes
            the video seals automatically.
          </Typography>

          {publishError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {publishError}
            </Alert>
          )}
          {sealError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sealError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setConfirmRepublishOpen(true)}
              disabled={publishMutation.isPending}
            >
              {publishMutation.isPending ? 'Starting...' : 'Republish'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmSealOpen(true)}
              disabled={sealMutation.isPending}
            >
              {sealMutation.isPending ? 'Sealing...' : 'Seal Now'}
            </Button>
          </Box>

          {hostVerifiedBanner}
          <ChainAnchorBlock video={video} />
        </Box>
      )}

      {/* Sealed — explicitly, or because the window ran out. No editing
          affordances at all: no Republish, no Seal, nothing that touches
          content. Only the read-only host check above remains available. */}
      {phase === 'sealed' && info && (
        <Box>
          {rootHashBlock}

          <Typography variant="body2" color="text.secondary">
            {explicitlySealed
              ? `Sealed ${formatDate(info.sealed_at)}`
              : `Sealed automatically — 7 days after the last correction (${formatDate(info.published_at)}), the window closed ${formatDate(info.window_expires)}`}
            {' · '}revision {info.revision} · {info.rendition_count} renditions ·{' '}
            {info.segment_count} segments · {formatBytes(info.total_bytes)}
          </Typography>

          {hostVerifiedBanner}
          <ChainAnchorBlock video={video} />
        </Box>
      )}

      {/* Republish confirmation */}
      <Dialog open={confirmRepublishOpen} onClose={() => setConfirmRepublishOpen(false)}>
        <DialogTitle>Republish this video?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Republishing re-downloads and re-hashes the video's media — using a quick
            metadata-only check when nothing appears to have changed, unless you force a full
            re-hash below — then produces a new root hash and resets the 7-day correction window
            starting now. The current manifest keeps serving until the replacement is stored, so
            a failed republish leaves today's root hash intact.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={forceFull}
                onChange={e => setForceFull(e.target.checked)}
              />
            }
            label="Force a complete re-hash of every segment (skip the metadata-only shortcut)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRepublishOpen(false)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              setConfirmRepublishOpen(false)
              publishMutation.mutate(forceFull)
              setForceFull(false)
            }}
          >
            Republish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seal confirmation */}
      <Dialog open={confirmSealOpen} onClose={() => setConfirmSealOpen(false)}>
        <DialogTitle>Seal this video?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sealing freezes this video's title, description, participants, rotation adjustment,
            subtitle offset, subtitles and speaker attributions. <strong>This cannot be undone —
            there is no unseal</strong>, here or, once the chain is wired up, on the blockchain
            either. Where the video is hosted can still change afterwards; Re-verify is how you
            prove a new host serves the same bytes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSealOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmSealOpen(false)
              sealMutation.mutate()
            }}
          >
            Seal permanently
          </Button>
        </DialogActions>
      </Dialog>
    </SectionWrapper>
  )
}

export default PublicationSection
